import React, { useEffect, useState, useRef } from 'react';
import { X, Download, Printer } from 'lucide-react';

/**
 * Modal de prévisualisation d'un PDF généré à la volée (jsPDF).
 * - Le navigateur affiche le PDF dans une iframe via blob URL (PDF.js natif).
 * - Boutons Télécharger / Imprimer / Fermer.
 * - Le blob URL est libéré (URL.revokeObjectURL) à la fermeture.
 *
 * Props :
 *   doc       : instance jsPDF (déjà rempli)
 *   fileName  : nom de fichier proposé pour le téléchargement
 *   onClose   : callback de fermeture
 */
const PdfPreviewModal = ({ doc, fileName, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!doc) return;
    const url = doc.output('bloburl');
    setBlobUrl(url);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [doc]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleDownload = () => {
    if (doc && fileName) doc.save(fileName);
  };

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  if (!doc) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="bg-white rounded-xl shadow-xl flex flex-col w-full max-w-6xl h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-700 truncate">{fileName}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
              title="Télécharger le PDF"
            >
              <Download size={14} /> Télécharger
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              title="Imprimer"
            >
              <Printer size={14} /> Imprimer
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
              title="Fermer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body : iframe PDF */}
        <div className="flex-1 bg-gray-100 overflow-hidden">
          {blobUrl ? (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              title={fileName}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
              Génération du PDF...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfPreviewModal;
